import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';


const PerformanceChart = () => {
  const [chartType, setChartType] = useState('response');
  const [timeRange, setTimeRange] = useState('1h');
  const [data, setData] = useState([]);

  const generateMockData = () => {
    const now = new Date();
    const dataPoints = timeRange === '1h' ? 60 : timeRange === '6h' ? 72 : 144;
    const interval = timeRange === '1h' ? 60000 : timeRange === '6h' ? 300000 : 600000;

    return Array.from({ length: dataPoints }, (_, i) => {
      const timestamp = new Date(now - (dataPoints - i) * interval);
      return {
        time: timestamp?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        apiResponse: Math.floor(Math.random() * 200 + 150),
        swapProcessing: Math.floor(Math.random() * 300 + 200),
        walletConnectivity: Math.floor(Math.random() * 150 + 100),
        cardProcessing: Math.floor(Math.random() * 250 + 180),
        errorRate: Math.random() * 2 + 0.5,
        throughput: Math.floor(Math.random() * 500 + 1000)
      };
    });
  };

  useEffect(() => {
    setData(generateMockData());

    const interval = setInterval(() => {
      setData(prev => {
        const newData = [...prev?.slice(1)];
        const lastTime = new Date();
        newData?.push({
          time: lastTime?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          apiResponse: Math.floor(Math.random() * 200 + 150),
          swapProcessing: Math.floor(Math.random() * 300 + 200),
          walletConnectivity: Math.floor(Math.random() * 150 + 100),
          cardProcessing: Math.floor(Math.random() * 250 + 180),
          errorRate: Math.random() * 2 + 0.5,
          throughput: Math.floor(Math.random() * 500 + 1000)
        });
        return newData;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [timeRange]);

  const chartConfigs = {
    response: {
      title: 'API Response Times',
      lines: [
        { key: 'apiResponse', name: 'API Gateway', color: '#2563EB' },
        { key: 'swapProcessing', name: 'Swap Engine', color: '#7C3AED' },
        { key: 'walletConnectivity', name: 'Wallet Service', color: '#10B981' },
        { key: 'cardProcessing', name: 'Card Rail', color: '#F59E0B' }
      ],
      unit: 'ms',
      yAxisDomain: [0, 600]
    },
    error: {
      title: 'Error Rate',
      lines: [
        { key: 'errorRate', name: 'Error Rate', color: '#EF4444' }
      ],
      unit: '%',
      yAxisDomain: [0, 5]
    },
    throughput: {
      title: 'Transaction Throughput',
      lines: [
        { key: 'throughput', name: 'Transactions/min', color: '#2563EB' }
      ],
      unit: 'tx/min',
      yAxisDomain: [0, 2000]
    }
  };

  const currentConfig = chartConfigs?.[chartType];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-xs text-muted-foreground caption mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 mb-1">
              <span className="text-xs font-medium" style={{ color: entry?.color }}>
                {entry?.name}:
              </span>
              <span className="text-xs font-bold font-data" style={{ color: entry?.color }}>
                {entry?.value?.toFixed(entry?.dataKey === 'errorRate' ? 2 : 0)}{currentConfig?.unit}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <h2 className="text-base md:text-lg font-semibold text-foreground">{currentConfig?.title}</h2>
        
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <div className="flex gap-2">
            {Object.keys(chartConfigs)?.map((type) => (
              <button
                key={type}
                onClick={() => setChartType(type)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-250 ${
                  chartType === type
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'
                }`}
              >
                {type?.charAt(0)?.toUpperCase() + type?.slice(1)}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            {['1h', '6h', '24h']?.map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-250 ${
                  timeRange === range
                    ? 'bg-secondary text-secondary-foreground'
                    : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="w-full h-[300px] md:h-[350px] lg:h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.3} />
            <XAxis 
              dataKey="time" 
              stroke="var(--color-muted-foreground)"
              tick={{ fill: 'var(--color-muted-foreground)', fontSize: 11 }}
              tickLine={{ stroke: 'var(--color-border)' }}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              tick={{ fill: 'var(--color-muted-foreground)', fontSize: 11 }}
              tickLine={{ stroke: 'var(--color-border)' }}
              domain={currentConfig?.yAxisDomain}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
              formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
            />
            {currentConfig?.lines?.map((line) => (
              <Line
                key={line?.key}
                type="monotone"
                dataKey={line?.key}
                name={line?.name}
                stroke={line?.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: line?.color }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        {currentConfig?.lines?.map((line) => {
          const latestValue = data?.[data?.length - 1]?.[line?.key] || 0;
          return (
            <div key={line?.key} className="p-3 rounded-lg bg-muted/30">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: line?.color }} />
                <span className="text-xs text-muted-foreground caption">{line?.name}</span>
              </div>
              <span className="text-lg md:text-xl font-bold text-foreground font-data">
                {latestValue?.toFixed(line?.key === 'errorRate' ? 2 : 0)}{currentConfig?.unit}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PerformanceChart;